import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Edit2, Trash2, Plus, Minus, Loader2 } from 'lucide-react';
import { clsx } from 'clsx';
import { adminApi } from '../api/client';
import type { User } from '../types';

export default function UsersPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const limit = 10;

  const { data, isLoading } = useQuery({
    queryKey: ['admin-users', page, search],
    queryFn: () => adminApi.getUsers({ limit, offset: page * limit, search }),
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<User> }) => adminApi.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setEditingUser(null);
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteUser(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  const setCreditsMutation = useMutation({
    mutationFn: ({ id, credits }: { id: string; credits: number }) => adminApi.setUserCredits(id, credits),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] }),
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(0);
  };

  const totalPages = Math.ceil((data?.total || 0) / limit);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">用戶管理</h1>
          <p className="text-gray-400 mt-1">管理系統用戶與配額</p>
        </div>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex space-x-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="搜尋用戶..."
            className="input pl-10"
          />
        </div>
        <button type="submit" className="btn-primary">搜尋</button>
      </form>

      {/* Table */}
      <div className="card overflow-hidden p-0">
        <table className="w-full">
          <thead className="bg-gray-700/50">
            <tr>
              <th className="table-header">用戶</th>
              <th className="table-header">等級</th>
              <th className="table-header">Credits</th>
              <th className="table-header">註冊時間</th>
              <th className="table-header">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {isLoading ? (
              <tr>
                <td colSpan={5} className="px-6 py-12 text-center">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary-500" />
                </td>
              </tr>
            ) : (data?.users || []).map((user) => (
              <tr key={user.id} className="hover:bg-gray-700/30">
                <td className="table-cell">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center">
                      <span className="text-sm">{user.name?.[0]?.toUpperCase()}</span>
                    </div>
                    <div>
                      <p className="font-medium text-white">{user.name}</p>
                      <p className="text-xs text-gray-400">{user.email}</p>
                    </div>
                  </div>
                </td>
                <td className="table-cell">
                  <span className={clsx(
                    'px-2 py-1 text-xs rounded-full',
                    user.tier === 'enterprise' ? 'bg-purple-500/20 text-purple-400' :
                    user.tier === 'pro' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-gray-500/20 text-gray-400'
                  )}>
                    {user.tier}
                  </span>
                </td>
                <td className="table-cell">
                  <div className="flex items-center space-x-2">
                    <span className="font-mono">{user.credits}</span>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => setCreditsMutation.mutate({ id: user.id, credits: user.credits + 10 })}
                        className="p-1 text-green-400 hover:bg-green-500/20 rounded"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setCreditsMutation.mutate({ id: user.id, credits: Math.max(0, user.credits - 10) })}
                        className="p-1 text-red-400 hover:bg-red-500/20 rounded"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </td>
                <td className="table-cell">
                  {new Date(user.createdAt).toLocaleDateString('zh-TW')}
                </td>
                <td className="table-cell">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setEditingUser(user)}
                      className="p-2 text-gray-400 hover:text-white hover:bg-gray-600 rounded-lg"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(`確定要刪除用戶 ${user.name}？`)) {
                          deleteUserMutation.mutate(user.id);
                        }
                      }}
                      className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-400">
            共 {data?.total} 個用戶
          </p>
          <div className="flex space-x-2">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="btn-secondary disabled:opacity-50"
            >
              上一頁
            </button>
            <span className="px-4 py-2 text-gray-400">
              {page + 1} / {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="btn-secondary disabled:opacity-50"
            >
              下一頁
            </button>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card w-full max-w-md">
            <h3 className="text-xl font-bold text-white mb-4">編輯用戶</h3>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                updateUserMutation.mutate({
                  id: editingUser.id,
                  data: {
                    name: formData.get('name') as string,
                    tier: formData.get('tier') as 'free' | 'pro' | 'enterprise',
                    credits: parseInt(formData.get('credits') as string),
                  },
                });
              }}
              className="space-y-4"
            >
              <div>
                <label className="block text-sm text-gray-400 mb-1">名稱</label>
                <input name="name" defaultValue={editingUser.name} className="input" required />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">等級</label>
                <select name="tier" defaultValue={editingUser.tier} className="input">
                  <option value="free">Free</option>
                  <option value="pro">Pro</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Credits</label>
                <input name="credits" type="number" defaultValue={editingUser.credits} className="input" required />
              </div>
              <div className="flex space-x-3 pt-4">
                <button type="button" onClick={() => setEditingUser(null)} className="btn-secondary flex-1">
                  取消
                </button>
                <button type="submit" className="btn-primary flex-1" disabled={updateUserMutation.isPending}>
                  {updateUserMutation.isPending ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : '保存'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
